import axios from 'axios';
import api from './index';
import { Vehicle } from './vehicles';
import { Employee } from './Employee';
import { resolvePublicFileUrl } from '../config.api';

export type TripStatus =
  | 'pendente'
  | 'aprovada'
  | 'rejeitada'
  | 'em_andamento'
  | 'concluida'
  | 'cancelada';

export interface TripImage {
  id: number;
  base64: string | null;
  url?: string | null;
  storage_key?: string | null;
  mime_type?: string | null;
  size_bytes?: number | null;
  type: 'TRIP_CHECKIN' | 'TRIP_CHECKOUT';
  created_at: string;
  updated_at: string;
}

export function getTripImageSrc(img: TripImage | null | undefined): string {
  if (!img) return '';
  if (typeof img.url === 'string' && img.url.trim().length > 0) {
    return resolvePublicFileUrl(img.url);
  }
  const base64 = (typeof img.base64 === 'string' ? img.base64 : '').trim();
  if (!base64) return '';
  return base64.startsWith('data:') ? base64 : `data:image/jpeg;base64,${base64}`;
}

export interface Trip {
  id: number;
  vehicle_id: number;
  employee_id: number;
  start_date: string;
  end_date?: string;
  final_mileage?: number;
  purpose?: string;
  destination?: string;
  status: TripStatus;
  usage_duration_minutes?: number;
  cancellation_reason?: string;
  created_at: string;
  updated_at: string;
  images?: TripImage[];
  vehicle?: Vehicle;
  employee?: Employee;
  report?: unknown;
}

export interface AcceptTripResponse {
  updatedTrip: Trip;
  redirectToCheckin: boolean;
  checkinData: {
    vehicle_id: number;
    employee_id: number;
    trip_id: number;
  };
}

export interface CreateTripData {
  vehicle_id: number;
  employee_id: number;
  start_date: string;
  end_date?: string;
  purpose?: string;
  destination?: string;
  status?: TripStatus;
  cancellation_reason?: string;
}

export type UpdateTripData = Partial<CreateTripData>;

export interface FinalizeTripData {
  final_mileage: number;
  usage_duration_minutes: number;
  checkoutImage?: string;
}

export class ApiError extends Error {
  statusCode?: number;
  constructor(message: string, statusCode?: number) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
  }
}

const tripsUrl = '/trips';

const parseApiError = (error: unknown): never => {
  if (axios.isAxiosError(error)) {
    const message =
      (typeof error.response?.data?.message === 'string' && error.response.data.message) ||
      (Array.isArray(error.response?.data?.message) && error.response?.data?.message.join(', ')) ||
      error.message ||
      'Erro na requisição de viagens';

    throw new ApiError(message, error.response?.status);
  }

  if (error instanceof Error) {
    throw new ApiError(error.message);
  }

  throw new ApiError('Erro na requisição de viagens');
};

export const tripService = {
  async getAll(): Promise<Trip[]> {
    try {
      const response = await api.get(tripsUrl);
      return response.data;
    } catch (error) {
      return parseApiError(error);
    }
  },
  async getById(id: number): Promise<Trip> {
    try {
      const response = await api.get(`${tripsUrl}/${id}`);
      return response.data;
    } catch (error) {
      return parseApiError(error);
    }
  },
  async create(data: CreateTripData): Promise<Trip> {
    try {
      const response = await api.post(tripsUrl, data);
      return response.data;
    } catch (error) {
      return parseApiError(error);
    }
  },
  async update(id: number, data: UpdateTripData): Promise<Trip> {
    try {
      const response = await api.put(`${tripsUrl}/${id}`, data);
      return response.data;
    } catch (error) {
      return parseApiError(error);
    }
  },
  async delete(id: number): Promise<void> {
    try {
      await api.delete(`${tripsUrl}/${id}`);
    } catch (error) {
      return parseApiError(error);
    }
  },
  async approve(id: number): Promise<Trip> {
    try {
      const response = await api.put(`${tripsUrl}/${id}/approve`);
      return response.data;
    } catch (error) {
      return parseApiError(error);
    }
  },
  async reject(id: number, reason: string): Promise<Trip> {
    try {
      const response = await api.put(`${tripsUrl}/${id}/reject`, { reason });
      return response.data;
    } catch (error) {
      return parseApiError(error);
    }
  },
  async accept(id: number): Promise<AcceptTripResponse> {
    try {
      const response = await api.put(`${tripsUrl}/${id}/accept`);
      return response.data;
    } catch (error) {
      return parseApiError(error);
    }
  },
  async finalize(id: number, data: FinalizeTripData): Promise<Trip> {
    try {
      const response = await api.put(`${tripsUrl}/${id}/finalize`, data);
      return response.data;
    } catch (error) {
      return parseApiError(error);
    }
  },
};
